import { isValidElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, MouseEvent } from "react";
import type { Menu } from "primereact/menu";
import type { MenuItem } from "primereact/menuitem";
import type { Tooltip } from "primereact/tooltip";

import { GANTT_LAYOUT_PRESETS } from "@/ds";

import { GANTT_ACTIONS_LABEL, GANTT_COLLAPSE_LABEL, GANTT_EXPAND_LABEL } from "./gantt.constants";
import { positionDate, positionToday, scaleUnits } from "./gantt-scale";
import type {
  GanttActionsView,
  GanttBodyItem,
  GanttColumnView,
  GanttContextValue,
  GanttGridLineView,
  GanttGroup,
  GanttHeaderBandView,
  GanttHeaderRow,
  GanttHeaderRowView,
  GanttMarkerItem,
  GanttProps,
  GanttRow,
  GanttRowActions,
  GanttScale,
  GanttScaleBand,
  GanttViewModel,
} from "./types-gantt";

const DEFAULT_HEADER: Record<GanttScale["unit"], GanttHeaderRow[]> = {
  month: ["year", "month"],
  day: ["month-year", "day"],
};

const DEFAULT_UNIT_MIN_WIDTH: Record<GanttScale["unit"], string> = {
  month: `${GANTT_LAYOUT_PRESETS.compact.monthWidthRem}rem`,
  // Provisório: a escala por dia não tem frame no Figma (pendência de design); a largura do dia sai de lá quando houver.
  day: "var(--ds-spacing-8)",
};

const percent = (value: number) => `${value}%`;

/** `calc()` da soma das larguras; `0` sem nenhuma. */
function sumWidths(widths: string[]): string {
  return widths.length === 0 ? "0" : `calc(${widths.join(" + ")})`;
}

function isMenuModel(actions: GanttRowActions | undefined): actions is MenuItem[] {
  return (
    Array.isArray(actions) &&
    actions.every((item) => item !== null && typeof item === "object" && !isValidElement(item))
  );
}

function bandsOf(scale: GanttScale, kind: GanttHeaderRow): GanttHeaderBandView[] {
  const source: Record<GanttHeaderRow, GanttScaleBand[]> = {
    year: scale.years,
    month: scale.months,
    "month-year": scale.months,
    day: scale.days,
  };
  return source[kind].map((band) => ({
    key: band.key,
    label: kind === "month-year" ? band.longLabel : band.label,
    style: { left: percent(band.left), width: percent(band.width) },
    divider: band.left > 0 || undefined,
  }));
}

/**
 * ViewModel do Gantt: escala do cabeçalho, deslocamento das colunas sticky, grupos (abertos/recolhidos), posição do
 * Hoje e dos marcos, e o ⋮ de cada linha (um `Menu` só, com os itens da linha clicada). Sem JSX.
 */
export function useGantt({
  scale,
  label,
  columns,
  rows,
  groups,
  expandedGroups,
  defaultExpandedGroups,
  onExpandedGroupsChange,
  markers,
  today,
  todayLabel,
  header,
  grid = "month",
  withActions = false,
  unitMinWidth,
  corpoRola = false,
  testId = "gantt",
  className,
  style,
  classNames,
  ...props
}: GanttProps): GanttViewModel {
  const menuRef = useRef<Menu>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<Tooltip>(null);
  const [menuModel, setMenuModel] = useState<MenuItem[]>([]);
  const [internalExpanded, setInternalExpanded] = useState<string[]>(
    () => defaultExpandedGroups ?? (groups ?? []).map((group) => group.key),
  );
  const expanded = expandedGroups ?? internalExpanded;

  // Com `focusDay`, o eixo rola até esse dia (as colunas fixas são sticky, então o deslocamento é só o do eixo).
  useEffect(() => {
    const scroller = scrollerRef.current;
    const track = scaleRef.current;
    if (!scroller || !track || scale.focusDay === undefined) return;
    const offset = ((scale.focusDay - scale.startDay) / scale.totalDays) * track.offsetWidth;
    scroller.scrollLeft = Math.max(offset, 0);
  }, [scale]);

  // O Tooltip do Prime liga os alvos só ao montar: barra criada depois (outra linha, marca da simulação) fica sem ele.
  useEffect(() => {
    tooltipRef.current?.updateTargetEvents();
  }, [rows]);

  const toggleGroup = useCallback(
    (key: string) => {
      const next = expanded.includes(key)
        ? expanded.filter((current) => current !== key)
        : [...expanded, key];
      if (!expandedGroups) setInternalExpanded(next);
      onExpandedGroupsChange?.(next);
    },
    [expanded, expandedGroups, onExpandedGroupsChange],
  );

  const openMenu = useCallback((event: MouseEvent<HTMLElement>, items: MenuItem[]) => {
    setMenuModel(items);
    menuRef.current?.toggle(event);
  }, []);

  const layout = useMemo(() => {
    const widths = columns.map((column) => column.width);
    const fixedWidth = sumWidths(widths);
    const actionsWidth = withActions ? "var(--ds-size-gantt-actions)" : "0rem";
    const unitWidth = unitMinWidth ?? DEFAULT_UNIT_MIN_WIDTH[scale.unit];
    const units = scaleUnits(scale).length;

    return {
      fixedWidth,
      columnStyles: columns.map((column, index): CSSProperties => ({
        left: sumWidths(widths.slice(0, index)),
        width: column.width,
        flexBasis: column.width,
      })),
      tableStyle: {
        minWidth: `calc(${fixedWidth} + ${actionsWidth} + ${units} * ${unitWidth})`,
      } satisfies CSSProperties,
    };
  }, [columns, withActions, unitMinWidth, scale]);

  const headerRows = useMemo<GanttHeaderRowView[]>(
    () =>
      (header ?? DEFAULT_HEADER[scale.unit]).map((kind, index, all) => ({
        kind,
        position: all.length > 1 && index === 0 ? "first" : undefined,
        bands: bandsOf(scale, kind),
      })),
    [header, scale],
  );

  const gridLines = useMemo<GanttGridLineView[]>(() => {
    if (grid === "none") return [];
    const bands = grid === "unit" ? scaleUnits(scale) : scale.months;
    return bands
      .filter((band) => band.left > 0)
      .map((band) => ({ key: band.key, style: { left: percent(band.left) } }));
  }, [grid, scale]);

  const todayLeft = today ? positionToday(scale, today) : null;
  const todayStyle = useMemo(
    () => (todayLeft === null ? null : { left: percent(todayLeft) }),
    [todayLeft],
  );

  const body = useMemo<GanttBodyItem[]>(() => {
    const groupByKey = new Map((groups ?? []).map((group) => [group.key, group]));
    const visible = (row: GanttRow) => {
      const group = row.groupKey ? groupByKey.get(row.groupKey) : undefined;
      if (!group) return true;
      const open = expanded.includes(group.key);
      return (row.showWhen ?? "expanded") === "collapsed" ? !open : open;
    };

    const ordered: (GanttRow | GanttGroup)[] = groups
      ? [
          ...rows.filter((row) => !row.groupKey || !groupByKey.has(row.groupKey)),
          ...groups.flatMap((group) => [
            group,
            ...rows.filter((row) => row.groupKey === group.key),
          ]),
        ]
      : rows;

    const items = ordered.filter((item) =>
      "cells" in item ? visible(item) : (item.variant ?? "header") === "header",
    );
    const rowIndex = new Map(
      items.flatMap((item, index) => ("cells" in item ? [[item.key, index] as const] : [])),
    );

    const markerViews = (markers ?? []).flatMap((marker) => {
      const left = positionDate(scale, marker.date);
      if (left === null) return [];
      const markerTestId = marker.testId ?? `${testId}-${marker.kind}`;
      return [
        {
          marker,
          view: {
            key: marker.key,
            kind: marker.kind,
            style: { left: percent(left) },
            testId: markerTestId,
            withStar: marker.kind === "start",
          },
          until:
            marker.kind === "limit"
              ? (rowIndex.get(marker.untilRow ?? "") ?? items.length - 1)
              : (rowIndex.get(marker.row) ?? -1),
        },
      ];
    });

    return items.map((item, index): GanttBodyItem => {
      if (!("cells" in item)) {
        const open = expanded.includes(item.key);
        return {
          type: "group",
          key: item.key,
          label: item.label,
          testId: item.testId,
          toggleLabel: item.toggleLabel ?? (open ? GANTT_COLLAPSE_LABEL : GANTT_EXPAND_LABEL),
          expanded: open,
          toggleIcon: open ? "chevron-down" : "chevron-right",
          onToggle: () => toggleGroup(item.key),
        };
      }

      const actionLabel = item.actionLabel ?? GANTT_ACTIONS_LABEL;
      const rowActions = item.actions;
      const actions: GanttActionsView = item.hideActions
        ? { kind: "hidden" }
        : isMenuModel(rowActions)
          ? rowActions.length > 0
            ? {
                kind: "menu",
                label: actionLabel,
                testId: `${testId}-row-actions`,
                onOpen: (event) => openMenu(event, rowActions),
              }
            : { kind: "disabled", label: actionLabel }
          : rowActions === undefined || rowActions === null
            ? { kind: "disabled", label: actionLabel }
            : { kind: "node", node: rowActions };

      return {
        type: "row",
        key: item.key,
        testId: item.testId,
        className: item.className,
        style: { minHeight: item.minHeight },
        content: item.content,
        actions,
        cells: columns.map((column, cellIndex) => {
          const span = item.cellRowSpans?.[cellIndex] ?? 1;
          const skipped = item.skipCells?.includes(cellIndex) ?? false;
          return {
            key: column.key,
            role: cellIndex === 0 ? "rowheader" : "cell",
            content: skipped ? null : item.cells[cellIndex],
            className: column.cellClassName,
            spanned: span > 1,
            style:
              span > 1
                ? ({
                    ...layout.columnStyles[cellIndex],
                    "--gantt-row-span": span,
                  } as CSSProperties)
                : (layout.columnStyles[cellIndex] ?? {}),
          };
        }),
        markers: markerViews
          .filter(({ marker, until }) =>
            marker.kind === "limit" ? index <= until : until === index,
          )
          .map(({ view }) => view),
      };
    });
  }, [groups, rows, expanded, markers, scale, testId, columns, layout, toggleGroup, openMenu]);

  const headerMarkers = useMemo<GanttMarkerItem[]>(
    () =>
      (markers ?? []).flatMap((marker) => {
        const left = marker.kind === "limit" ? positionDate(scale, marker.date) : null;
        return left === null
          ? []
          : [
              {
                key: marker.key,
                kind: marker.kind,
                style: { left: percent(left) },
                testId: `${marker.testId ?? `${testId}-${marker.kind}`}-head`,
                withStar: false,
              },
            ];
      }),
    [markers, scale, testId],
  );

  const context = useMemo<GanttContextValue>(
    () => ({
      classNames: classNames ?? {},
      withActions,
      actionsHeaderLabel: GANTT_ACTIONS_LABEL,
      columnCount: columns.length + 1 + (withActions ? 1 : 0),
      headerRowCount: headerRows.length,
      columns: columns.map((column, index): GanttColumnView => ({
        key: column.key,
        header: column.header,
        style: layout.columnStyles[index] ?? {},
      })),
      headerRows,
      headerMarkers,
      gridLines,
      todayStyle,
      todayLabel: todayStyle ? todayLabel : undefined,
      todayTestId: `${testId}-today`,
      todayLabelTestId: `${testId}-today-label`,
      todayGroupTestId: `${testId}-today-group`,
      headerTestId: `${testId}-header`,
      groupToggleTestId: `${testId}-group-toggle`,
      body,
      scaleRef,
    }),
    [
      classNames,
      withActions,
      columns,
      layout,
      headerRows,
      headerMarkers,
      gridLines,
      todayStyle,
      todayLabel,
      testId,
      body,
      scaleRef,
    ],
  );

  return {
    context,
    testId,
    className,
    style: { ...({ "--gantt-fixed": layout.fixedWidth } as CSSProperties), ...style },
    corpoRola,
    rest: props,
    tableLabel: label,
    tableStyle: layout.tableStyle,
    scrollerTestId: `${testId}-scroller`,
    menuRef,
    scrollerRef,
    menuModel,
    menuTestId: `${testId}-row-menu`,
    tooltipRef,
  };
}
